import { useMutation, useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/providers/ToastProvider";
import type { UpdateUserPermissionsPayload } from "@/api/(requests)/permissionsTyped";
import type { UserPermissions, UUID } from "@/core/types";

import { permissionsKeys } from "../keyFactory";
import { updateUserPermissions } from "../mutationFunctions/updateUserPermissions";

type MutationContext = { previous: UserPermissions | undefined };

export function useUpdateUserPermissions(userId: UUID, providerName: string) {
  const queryClient = useQueryClient();
  const queryKey = permissionsKeys.userPermissions(userId);

  return useMutation<UserPermissions, unknown, UpdateUserPermissionsPayload, MutationContext>({
    mutationFn: (payload: UpdateUserPermissionsPayload) => updateUserPermissions(userId, payload),
    mutationKey: permissionsKeys.updateUserPermissions(userId),
    onError: (_err, _payload, context) => {
      if (context?.previous) {
        const { previous } = context;

        queryClient.setQueryData<UserPermissions>(queryKey, previous);
      }

      showToast([
        {
          heading: `Failed to update co-signature requirement for ${providerName}. Please try again.`,
          variant: "error",
        },
      ]);
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<UserPermissions>(queryKey);

      // Optimistic update only for toggling off. Toggling on is intentionally deferred to
      // onSettled: the payload lacks display-only metadata fields (assigned_supervisor_name,
      // state_updated_by_name, etc.) needed to construct a valid optimistic cache entry.
      if (!payload.co_sign.is_required) {
        queryClient.setQueryData<UserPermissions>(queryKey, (old) => {
          if (!old) return old;

          return { ...old, co_sign: { is_required: false } };
        });
      }

      return { previous };
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
